import {
  Message,
  DomainMessagesTypes,
  isPullFailedMessage,
} from 'shared/domain/messages/message';
import type { AnyChannelContract } from 'shared/channels/contract';
import { swSignals, type PublishedEvent } from 'serviceWorker/events/swSignals';

/**
 * Resolves with the next `messageType` the worker publishes on the `contract`, or with
 * `pullFailed`: that pull will not broadcast `messageType`, so waiting for it
 * would hang forever. `hasEnded`, when given, is checked on each such
 * message: the wait goes on until it says what the caller waits for is over,
 * and rejects with its error when it cannot tell (e.g. IndexedDB failed).
 * Aborting `signal` stops the listening; the promise then never settles.
 */
export function waitForEndingMessage(
  contract: Pick<AnyChannelContract, 'key'>,
  messageType: DomainMessagesTypes,
  hasEnded?: () => Promise<boolean>,
  signal?: AbortSignal
): Promise<Message> {
  return new Promise((resolve, reject) => {
    let isSettled = false;
    const settle = (finish: () => void): void => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      unsubscribe();
      finish();
    };
    const unsubscribe = swSignals.eventPublished.subscribe(async ({ contractKey, event }: PublishedEvent): Promise<void> => {
      if (contractKey !== contract.key || (event.type !== messageType && !isPullFailedMessage(event))) {
        return;
      }
      if (hasEnded) {
        try {
          if (!(await hasEnded())) {
            return;
          }
        } catch (error) {
          settle(() => reject(error));
          return;
        }
      }
      settle(() => resolve(event));
    });
    signal?.addEventListener('abort', unsubscribe, { once: true });
  });
}

/**
 * Resolves on the next `messageType` published on the `contract`, or on
 * `pullFailed`. Callers then read whatever is in the repository.
 */
export function waitForMessage(
  contract: AnyChannelContract,
  messageType: DomainMessagesTypes,
  hasEnded?: () => Promise<boolean>
): () => Promise<void> {
  return async function wait(): Promise<void> {
    await waitForEndingMessage(contract, messageType, hasEnded);
  };
}
